import * as React from 'react';
import _exporter from '../../pages/exporter';
import { List, Pagination, useListController } from 'react-admin';
import { RaKeys, setRaStore } from '../../utils/common';
import RaBody from './RaBody';
import { TablePaginationProps } from '@mui/material';

const defaultProps = {
    useBody: true, // to render none row
    empty: false,
    pagination: [5, 10, 25, 50, 1000]
};

/** `exporter` config for {@link RaList}: which columns to export and the download filename. */
interface RaListExporterConfig {
    /** Ordered list of column names to include in the export. */
    columnsExport: string[];
    /** Download filename; falls back to the exporter's default when omitted. */
    filename?: string;
}

/** Props for {@link RaList} — react-admin's `List`/`ListView` props plus the extras below. */
interface RaListProps extends Omit<React.ComponentProps<typeof List>, 'empty' | 'pagination' | 'exporter' | 'queryOptions'> {
    /** Content to render inside the list (typically a `<Datagrid>`/`<AgGrid>`). */
    children: React.ReactElement;
    /** Use default (render nothing) row when the result set is empty; set `false` for a custom empty state. Default: `true`. */
    useBody?: boolean;
    /** Show react-admin's built-in empty state instead of the list. Default: `false`. */
    empty?: boolean;
    /** State: `['id asc', 'date desc']`, or a single field name for a single-column sort. */
    sortby?: string[] | string;
    /** Either an array of fields to query, e.g. `['event', 'venue']` (forwarded to the data
     *  provider as `queryOptions.meta.q`), or react-admin's native `queryOptions` object
     *  (e.g. `{ onSuccess }`) forwarded as-is to the underlying `useGetList` call. */
    queryOptions?: string[] | React.ComponentProps<typeof List>['queryOptions'];
    /** Exporter config: `{ columnsExport: [...], filename: 'download or default Export' }`. */
    exporter?: RaListExporterConfig;
    /** Rows-per-page options. Default: `[5, 10, 25, 50, 1000]`. */
    pagination?: number[];
}

/**
 * Wrapper around react-admin's `List` that adds: an empty-row body override ({@link RaBody}),
 * `sortby` state-driven default sort, an `exporter` shorthand backed by `pages/exporter`, and a
 * pagination control that hides row counts while `isEmptyData` is true.
 */
const RaList: React.FC<RaListProps> = (props) => {
    const {
        useBody = defaultProps.useBody,
        empty = defaultProps.empty,
        pagination = defaultProps.pagination,
        children, filters, queryOptions, sortby, exporter
    } = props;

    // react-admin's useListController options type doesn't include RaListProps' extra fields (sortby, queryOptions, etc.).
    const { setSort, resource, perPage, sort, data } = useListController({ ...props, storeKey: false, disableSyncWithLocation: true } as any);

    React.useEffect(() => {
        setRaStore(`${resource}.${RaKeys.isRefresh}`, true);
    }, []);

    React.useEffect(() => {
        if (Array.isArray(sortby)) {
            if (sortby.length === 1) {
                let _p = sortby[0].split(' ');
                // setSort's SortPayload type expects a plain field name, not react-admin's multi-field sort shape.
                setSort({ field: _p[0], order: _p[1].toUpperCase() as 'ASC' | 'DESC' } as any);
            } else setSort({ field: sortby as unknown as string } as any);
        }
    }, [sortby]);

    const isEmptyData = data && data.length === 1 && data[0].id === 0;

    return (
        <>
            <div style={{ marginTop: 5 }} />
            <List {...props}
                storeKey={props.storeKey || false} // For selection Ids
                debounce={0}
                actions={false}
                empty={empty as any} // List's `empty` prop is a ReactElement, not the boolean this component exposes
                perPage={perPage}
                sort={sort}
                pagination={<RaPagination pagination={pagination} isEmptyData={isEmptyData} />}
                emptyWhileLoading={empty}
                disableSyncWithLocation={true}
                queryOptions={Array.isArray(queryOptions) ? { meta: { q: queryOptions } } : queryOptions}
                filters={filters ? React.cloneElement(filters as any) : undefined} // cloneElement's overloads don't infer filters' element type
                exporter={exporter ? (records: unknown) => {
                    _exporter(records, exporter.columnsExport, exporter.filename);
                } : undefined}
            >
                {
                    useBody && isEmptyData ?
                        React.cloneElement(children, {
                            body: <RaBody /> // Render none row
                        })
                        : children
                }
            </List>
        </>
    );
};

/** Props for {@link RaPagination} — react-admin's `Pagination` props plus the extras below. */
interface RaPaginationProps extends Omit<React.ComponentProps<typeof Pagination>, 'rowsPerPageOptions'> {
    /** Rows-per-page options. Default: `[5, 10, 25, 50, 1000]`. */
    pagination?: number[];
    /** When true, hides the `"x-y of z"` label (used while showing the {@link RaBody} placeholder row). */
    isEmptyData?: boolean;
}

// Broaden the Pagination component so that TypeScript understands it accepts additional labelDisplayedRows
const BroadenedPagination = Pagination as React.ComponentType<
    React.ComponentProps<typeof Pagination> & Pick<TablePaginationProps, 'labelDisplayedRows' | 'rowsPerPageOptions'>
>;

/**
 * `Pagination` variant that accepts a custom `rowsPerPageOptions` array via `pagination`, and
 * blanks out the displayed-rows label while `isEmptyData` is true.
 */
export const RaPagination: React.FC<RaPaginationProps> = ({ pagination, isEmptyData, ...props }) =>
    <BroadenedPagination {...props}
        rowsPerPageOptions={pagination || defaultProps.pagination}
        labelDisplayedRows={(o: { from: number; to: number; count: number }) => isEmptyData ? '' : `${o.from}-${o.to} of ${new Intl.NumberFormat().format((o.count))}`}
    />;

export default RaList;
